import type { RootState } from 'src/redux/store';

import { useState, useEffect } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { isSuperAdminRole } from 'src/permissions/permission-engine';
import { useGetUserDtlMutation } from 'src/pages/setting/components/users/slice/userApi';
import { type IRole, useListRolesMutation } from 'src/pages/setting/components/users/slice/rbacApi';
import {
  useGetRoleChannelAccessQuery,
  useGetChannelFeatureFlagsQuery,
} from 'src/pages/setting/components/channel-feature-flags/slice/channelFeatureFlagApi';

/**
 * Tenant feature flags + current user's role channel-access rows (same inputs as the
 * role "Channel access" matrix). Used by Create Channel step 1 and Email provider step 2.
 */
export function useChannelWizardAccessContext() {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const userRole =
    loginApiData && typeof loginApiData === 'object' && 'role' in loginApiData
      ? (loginApiData as { role?: string }).role
      : undefined;
  const userId =
    loginApiData && typeof loginApiData === 'object' && 'id' in loginApiData
      ? (loginApiData as { id?: string }).id
      : undefined;
  const isSuperAdmin = isSuperAdminRole(userRole);

  const [resolvedRoleId, setResolvedRoleId] = useState<string | undefined>(undefined);

  const [getUserDtl] = useGetUserDtlMutation();
  const [listRoles] = useListRolesMutation();

  const {
    data: tenantFlags = [],
    isSuccess: flagsSuccess,
    isError: flagsError,
  } = useGetChannelFeatureFlagsQuery(undefined, { skip: !loginApiData });

  useEffect(() => {
    let cancelled = false;

    async function resolveRoleId() {
      if (!loginApiData) return;
      if (isSuperAdmin) {
        setResolvedRoleId('');
        return;
      }
      if (!userId) {
        setResolvedRoleId('');
        return;
      }
      setResolvedRoleId(undefined);
      try {
        const detail = await getUserDtl(userId).unwrap();
        if (cancelled) return;
        if (detail.roleId) {
          setResolvedRoleId(detail.roleId);
          return;
        }
        const res = await listRoles({ includePermissions: false }).unwrap();
        if (cancelled) return;
        const roles = ((res as any)?.list || (Array.isArray(res) ? res : [])) as IRole[];
        const match = roles.find((r) => r.name === userRole);
        setResolvedRoleId(match?.id ?? '');
      } catch {
        if (!cancelled) setResolvedRoleId('');
      }
    }

    void resolveRoleId();
    return () => {
      cancelled = true;
    };
  }, [loginApiData, isSuperAdmin, userId, userRole, getUserDtl, listRoles]);

  const roleGateReady = isSuperAdmin || resolvedRoleId !== undefined;

  const roleIdForQuery =
    !isSuperAdmin && resolvedRoleId && resolvedRoleId.length > 0 ? resolvedRoleId : undefined;

  const {
    data: roleAccess = [],
    isLoading: isRoleAccessLoading,
    isSuccess: isRoleAccessSuccess,
    isError: isRoleAccessError,
  } = useGetRoleChannelAccessQuery(
    { roleId: roleIdForQuery ?? '_skipped' },
    { skip: !roleIdForQuery, refetchOnMountOrArgChange: true }
  );

  const roleAccessReady =
    isSuperAdmin ||
    !roleIdForQuery ||
    isRoleAccessSuccess ||
    isRoleAccessError ||
    resolvedRoleId === '';

  const roleRowsForEligibility =
    isSuperAdmin || !roleIdForQuery || isRoleAccessError ? [] : roleAccess;

  const wizardAccessReady = (flagsSuccess || flagsError) && roleGateReady && roleAccessReady;

  const showWizardAccessLoader =
    Boolean(loginApiData) &&
    (!wizardAccessReady || (Boolean(roleIdForQuery) && isRoleAccessLoading));

  return {
    loginApiData,
    tenantFlags,
    flagsSuccess,
    roleGateReady,
    roleAccessReady,
    wizardAccessReady,
    showWizardAccessLoader,
    roleRowsForEligibility,
    isSuperAdmin,
    isRoleAccessLoading,
    roleIdForQuery,
  };
}
